/**
 * 点击音的共享合成层。
 *
 * 同一个「咔哒」有两种送达方式：直接接在 AudioContext 输出上，或者离线渲染成 WAV 交给
 * 媒体元素播放（部分安卓内核给 Web Audio 的渲染流单独上了低增益，音量明显偏小，
 * 详见 outputMode.ts）。两种方式必须发同一个声音，所以包络常量与节点构造收敛在这里。
 */

/** 点击音的包络与电平；改这里会同时影响两条输出通路 */
export const CLICK = {
  /** 噪声脉冲时长（秒），用于模拟木质敲击的瞬态 */
  noiseLength: 0.05,
  /** 噪声包络拐点（秒）：先快速掉到三成再拖尾，比单段指数衰减听上去更响 */
  noiseKnee: 0.015,
  /** 主音调时长（秒）；过短在手机上几乎听不见 */
  toneLength: 0.12,
  /** 主音调包络拐点（秒） */
  toneKnee: 0.03,
  /** 进入软限幅前的峰值电平（重音 / 弱音）；留足了余量，重叠时也不会超出 WaveShaper 的 ±1 定义域 */
  tonePeakAccent: 0.5,
  tonePeak: 0.36,
  noisePeakAccent: 0.42,
  noisePeak: 0.3,
  /** 软限幅 drive：tanh 饱和的推升倍数，3.2 时最坏重叠峰值约 0.95、不削顶 */
  softClipDrive: 3.2,
  /** 软限幅曲线采样点数（奇数保证 x=0 恰好落在格点上，避免引入直流偏置） */
  softClipSamples: 2049,
} as const

/** 单次点击占用的总时长（秒），含 stop() 的收尾余量 */
export const CLICK_TAIL = 0.03

/** tanh 饱和曲线：小信号段拿到接近 drive 倍的增益，大信号平滑趋近满刻度 */
export function buildSoftClipCurve(drive: number, samples: number): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(samples)
  const norm = Math.tanh(drive)
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1
    curve[i] = Math.tanh(drive * x) / norm
  }
  return curve
}

/** 造一个配好的软限幅节点（无 makeup gain 的压缩器不能拿来抬响度，见 MetronomeSound 头注释） */
export function createSoftClipShaper(ctx: BaseAudioContext): WaveShaperNode {
  const shaper = ctx.createWaveShaper()
  shaper.curve = buildSoftClipCurve(CLICK.softClipDrive, CLICK.softClipSamples)
  shaper.oversample = '2x'
  return shaper
}

/** 预生成一条白噪声 buffer 供所有点击复用，避免每次点击重新填充 */
export function createNoiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const frameCount = Math.max(1, Math.floor(ctx.sampleRate * CLICK.noiseLength))
  const buffer = ctx.createBuffer(1, frameCount, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < frameCount; i++) {
    data[i] = Math.random() * 2 - 1
  }
  return buffer
}

/**
 * 把一次点击接到汇流点上。
 * @param t 音频时钟时刻（秒）
 */
export function scheduleClickNodes(
  ctx: BaseAudioContext,
  dest: AudioNode,
  noiseBuffer: AudioBuffer,
  t: number,
  isAccent: boolean,
): void {
  // 主音调：两段包络（快速掉到拐点后拖尾），同峰值下比单段衰减能量更足
  const mainOsc = ctx.createOscillator()
  const mainGain = ctx.createGain()
  const tonePeak = isAccent ? CLICK.tonePeakAccent : CLICK.tonePeak
  mainOsc.type = 'triangle'
  mainOsc.frequency.value = isAccent ? 1800 : 1200
  mainGain.gain.setValueAtTime(0, t)
  mainGain.gain.linearRampToValueAtTime(tonePeak, t + 0.002)
  mainGain.gain.exponentialRampToValueAtTime(tonePeak * 0.35, t + CLICK.toneKnee)
  mainGain.gain.exponentialRampToValueAtTime(0.0006, t + CLICK.toneLength)
  mainOsc.connect(mainGain)
  mainGain.connect(dest)
  mainOsc.start(t)
  mainOsc.stop(t + CLICK.toneLength + 0.02)

  // 木质敲击噪声（复用预生成 buffer）
  const noise = ctx.createBufferSource()
  noise.buffer = noiseBuffer
  const noiseFilter = ctx.createBiquadFilter()
  noiseFilter.type = 'bandpass'
  noiseFilter.frequency.value = isAccent ? 2000 : 1500
  // Q 越小通带越宽，通过的能量越多，手机小外放下更容易听见
  noiseFilter.Q.value = 0.8
  const noiseGain = ctx.createGain()
  const noisePeak = isAccent ? CLICK.noisePeakAccent : CLICK.noisePeak
  noiseGain.gain.setValueAtTime(0, t)
  noiseGain.gain.linearRampToValueAtTime(noisePeak, t + 0.002)
  noiseGain.gain.exponentialRampToValueAtTime(noisePeak * 0.3, t + CLICK.noiseKnee)
  noiseGain.gain.exponentialRampToValueAtTime(0.0006, t + CLICK.noiseLength)
  noise.connect(noiseFilter)
  noiseFilter.connect(noiseGain)
  noiseGain.connect(dest)
  noise.start(t)
  noise.stop(t + CLICK.noiseLength + 0.01)
}

const RIFF_TEXT = ['RIFF', 'WAVE', 'fmt ', 'data'] as const

/** Float32 音频渲染结果 → 16-bit PCM 单声道 WAV（媒体元素只认封装好的文件） */
export function encodeWavBlob(buffer: AudioBuffer): Blob {
  const data = buffer.getChannelData(0)
  const sampleRate = buffer.sampleRate
  const dataSize = data.length * 2
  const view = new DataView(new ArrayBuffer(44 + dataSize))
  const writeText = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
  }
  writeText(0, RIFF_TEXT[0])
  view.setUint32(4, 36 + dataSize, true)
  writeText(8, RIFF_TEXT[1])
  writeText(12, RIFF_TEXT[2])
  view.setUint32(16, 16, true) // fmt 块长度
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // 单声道
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true) // byteRate
  view.setUint16(32, 2, true) // blockAlign
  view.setUint16(34, 16, true) // bitsPerSample
  writeText(36, RIFF_TEXT[3])
  view.setUint32(40, dataSize, true)
  for (let i = 0; i < data.length; i++) {
    const clamped = Math.max(-1, Math.min(1, data[i]!))
    view.setInt16(44 + i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
  }
  return new Blob([view], { type: 'audio/wav' })
}
